/**
 * Command API client: submit command, stream widget events via SSE.
 * When VITE_COMMAND_API_URL is unset or empty, caller should use mock flow.
 */

import {
  recordRunCompleted,
  recordRunFailed,
  recordRunSubmitted,
} from './adminState';
import { getMarchHttpApiBase, getMarchRuntimeBearer, isMarchHttpApiConfigured } from './marchApiEnv';
import { fetchWithAuth } from './fetchWithAuth';
import { streamErrorDisplayText } from './streamErrorCopy';
import {
  SESSION_EXPIRED_MESSAGE,
  STREAM_RECONNECTING_MESSAGE,
} from '../auth/tokenLifecycle';

async function safeJson<T = any>(res: Response): Promise<T> {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const text = await res.text();
    throw new Error(`API returned an invalid JSON response. The endpoint may be temporarily unavailable or misconfigured. Expected JSON but got ${ct}: ${text.substring(0, 50)}...`);
  }
  try {
    return await res.json() as T;
  } catch (e) {
    throw new Error('API returned an invalid JSON response.');
  }
}


const getBase = (): string => getMarchHttpApiBase();
const getApiKey = (): string => (import.meta.env.VITE_COMMAND_API_KEY ?? '').trim();

const getBearer = (): string => getMarchRuntimeBearer();
const getMode = (): 'march' | 'command' | 'auto' => {
  const raw = (import.meta.env.VITE_API_MODE ?? 'auto').toString().trim().toLowerCase();
  if (raw === 'march' || raw === 'command') return raw;
  return 'auto';
};

const pendingLegacyStreams = new Map<
  string,
  {
    command: string;
    tenantId: string;
    patientId: string;
    options?: Record<string, unknown>;
  }
>();

function isMarchMode(): boolean {
  const mode = getMode();
  if (mode === 'march') return true;
  if (mode === 'command') return false;
  // Auto: prefer March endpoints in this repo, fall back to command API only when explicitly requested.
  return true;
}

/**
 * Headers for a stream request. Deliberately carries NO ``Last-Event-ID``.
 *
 * SSE event ids restart at 1 for every run (one hub per run server-side), so a
 * cursor is only meaningful WITHIN the run that produced it. This module used
 * to keep one global cursor, updated by every stream and replayed onto the
 * next request: turn 1 ended at id 57, turn 2 asked to resume after 57, and a
 * 9-step tool run never gets that far — so the client received nothing and the
 * bubble rendered empty (dev 2026-08-11: every command after the first).
 *
 * The cursor is now per-stream and only ever sent when a caller explicitly
 * asks to resume (``openCommandStream``'s ``opts.lastEventId``; refresh-resume
 * passes '0' to replay a run from its start).
 */
function streamExtraHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const apiKey = getApiKey();
  if (apiKey) h['X-API-Key'] = apiKey;
  return h;
}

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const apiKey = getApiKey();
  if (apiKey) h['X-API-Key'] = apiKey;
  const bearer = getBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

function jsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

/** Legacy POST /chat stream headers — same no-stale-cursor rule as above. */
function streamHeaders(): Record<string, string> {
  return authHeaders();
}

function synthesizeWidgetFromSse(
  eventName: string,
  data: Record<string, unknown>
): { type: string; data: Record<string, unknown> } | null {
  if (eventName !== 'orchestrator.tool_result') return null;

  const toolName = String(data.tool ?? data.tool_name ?? data.tool_id ?? '').trim();
  const status = String(data.status ?? '').trim().toLowerCase();
  const rawResult = data.result ?? data.tool_result;
  const result =
    rawResult && typeof rawResult === 'object' ? (rawResult as Record<string, unknown>) : null;

  if (status !== 'success' || !result) return null;

  // Export tools → synthesize a download widget
  if (toolName === 'run_export_pdf' || toolName === 'run_export_csv') {
    if (result.found !== true || !result.filename) return null;
    return {
      type: toolName === 'run_export_pdf' ? 'W_FILE_PDF' : 'W_EXPORT_LIST',
      data: {
        filename: String(result.filename),
        format: String(result.format ?? (toolName === 'run_export_pdf' ? 'pdf' : 'csv')),
        generated_at: result.generated_at ? String(result.generated_at) : undefined,
        message: result.message ? String(result.message) : undefined,
      },
    };
  }

  // Medications / encounters / conditions etc.: synthesize display-friendly widget from items
  // (Backend sends structured in response.metadata; this is fallback when that's missing)
  const itemTools: Record<string, { type: string; title: string; nameKey: string }> = {
    get_medications: { type: 'W_MEDICATIONS', title: 'Medications', nameKey: 'name' },
    get_encounters: { type: 'W_ENCOUNTERS', title: 'Clinical Visits', nameKey: 'type' },
    get_conditions: { type: 'W_CONDITIONS', title: 'Conditions', nameKey: 'display_name' },
    get_allergies: { type: 'W_ALLERGIES', title: 'Allergies', nameKey: 'substance' },
    get_observations: { type: 'W_OBSERVATIONS', title: 'Lab Results', nameKey: 'display_name' },
    get_procedures: { type: 'W_PROCEDURES', title: 'Procedures', nameKey: 'display_name' },
    get_immunizations: { type: 'W_IMMUNIZATIONS', title: 'Immunizations', nameKey: 'vaccine_name' },
    get_diagnostic_reports: { type: 'W_REPORTS', title: 'Reports', nameKey: 'display_name' },
    get_care_plans: { type: 'W_CAREPLANS', title: 'Care Plans', nameKey: 'display_name' },
  };
  const itemCfg = itemTools[toolName as keyof typeof itemTools];
  if (itemCfg) {
    const items = (result.items ?? result.rows ?? result.medications ?? result.encounters ?? []) as unknown[];
    if (Array.isArray(items) && items.length > 0) {
      return {
        type: itemCfg.type,
        data: {
          title: itemCfg.title,
          items: items.slice(0, 50).map((it: any) =>
            typeof it === 'object' && it !== null
              ? { ...it, _display: it[itemCfg.nameKey] ?? it.name ?? it.display_name ?? JSON.stringify(it).slice(0, 80) }
              : { _display: String(it) }
          ),
        },
      };
    }
  }

  if (toolName !== 'get_patient_demographics') return null;

  // Backend returned a deterministic string (field-only, verbatim, etc.) — stream is the sole UI;
  // skip W_FHIR_PATIENT so the client does not inject a "Here are … demographics." preamble.
  if (result.deterministic_reply === true) {
    return null;
  }

  // Narrow / focused answer (e.g. "what is his name?") — skip widget, let the
  // text answer stream through normally instead of rendering a demographics card.
  if (typeof result.message === 'string' && result.found === undefined) {
    return null;
  }

  const patientId = String(result.patient_id ?? '').trim();
  const found = result.found === true;

  return {
    type: 'W_FHIR_PATIENT',
    data: {
      configured: true,
      found,
      source: 'session_duckdb',
      patientId: patientId || String(data.patient_id ?? '').trim(),
      fhirId: patientId || undefined,
      name: String(result.name ?? result.full_name ?? '').trim() || undefined,
      birthDate: String(result.date_of_birth ?? '').trim() || undefined,
      gender: String(result.sex_at_birth ?? '').trim() || undefined,
      ageYears:
        typeof result.age_years === 'number'
          ? result.age_years
          : typeof result.age_years === 'string' && result.age_years.trim()
            ? Number(result.age_years)
            : undefined,
      maritalStatus: String(result.marital_status_text ?? '').trim() || undefined,
      message: found ? undefined : 'Patient demographics were not found in the session data.',
    },
  };
}

/**
 * Per-stream SSE cursor.
 *
 * Was a module global, which leaked one run's last id onto the NEXT run's
 * request and silenced it entirely (see streamExtraHeaders). Scoped to a
 * single ``readSseEvents`` call so a cursor can never outlive the run it
 * describes; callers that genuinely resume pass their own id explicitly.
 */
export interface SseCursor {
  lastEventId: string | null;
}

async function readSseEvents(
  response: Response,
  onEvent: (payload: {
    channel?: string;
    event?: string;
    data?: Record<string, unknown>;
    type?: string;
    done?: boolean;
    sseEvent?: string;
  }) => void,
  cursor: SseCursor = { lastEventId: null }
): Promise<void> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processChunks = (
    rawBuffer: string,
    { flushTrailing }: { flushTrailing: boolean }
  ): string => {
    const normalized = rawBuffer.replace(/\r\n/g, '\n');
    const parts = normalized.split('\n\n');
    const trailing = flushTrailing ? '' : (parts.pop() ?? '');
    const completeChunks = flushTrailing ? parts.filter((part) => part.trim().length > 0) : parts;

    for (const chunk of completeChunks) {
      const lines = chunk.split('\n');
      const idLine = lines.find((line) => line.startsWith('id:'));
      if (idLine) {
        cursor.lastEventId = idLine.slice(3).trim();
      }
      const eventLine = lines.find((line) => line.startsWith('event:'));
      const sseEvent = eventLine ? eventLine.slice(6).trim() : undefined;
      const dataLines = lines
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim());
      const raw = dataLines.join('\n');
      if (!raw) continue;
      try {
        onEvent({
          ...(JSON.parse(raw) as {
            channel?: string;
            event?: string;
            data?: Record<string, unknown>;
            type?: string;
            done?: boolean;
          }),
          sseEvent,
        });
      } catch {
        // Ignore keep-alive / partial non-JSON chunks.
      }
    }

    return trailing;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      if (buffer.trim()) {
        processChunks(buffer, { flushTrailing: true });
      }
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    buffer = processChunks(buffer, { flushTrailing: false });
  }
}

export function isCommandApiEnabled(): boolean {
  return isMarchHttpApiConfigured();
}

/** Base URL for the Command API (e.g. for building download links). */
export function commandApiBase(): string {
  return getBase();
}

/** Build full URL for export file download. Use when Command API is enabled. */
export function getExportDownloadUrl(exportId: string): string {
  return getBase() + '/api/exports/' + encodeURIComponent(exportId);
}

export interface PatientListItem {
  mrn: string;
  name: string;
  status: string;
  risk: string;
}

/** Entity list item from GET /v1/{assessments|alerts|redflags|exports|simulations|visits} (backend → DB). No hardcoded data when API is used. */
export interface EntityListItem {
  id: string;
  date: string;
  time?: string;
  type: string;
  title?: string | null;
  summary: string;
  chips: string[];
}

/** @deprecated use EntityListItem */
export type AssessmentListItem = EntityListItem;

export interface DigitalTwinLatestState {
  tenant_id?: string;
  entity_type?: string;
  entity_id?: string;
  latest_snapshot_id?: string;
  snapshot_ts?: string;
  effective_ts?: string;
  state?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface DigitalTwinSnapshot {
  snapshot_id?: string;
  entity_type?: string;
  entity_id?: string;
  snapshot_ts?: string;
  effective_ts?: string;
  state?: Record<string, unknown>;
  metadata?: {
    source_component?: string;
    trigger?: string;
    source_event_id?: string;
    [key: string]: unknown;
  } | null;
}

export interface DigitalTwinOverview {
  available: boolean;
  summary: string;
  patient: DigitalTwinLatestState | null;
  risk: DigitalTwinLatestState | null;
  workflow: DigitalTwinLatestState | null;
  recommendation: DigitalTwinLatestState | null;
  patient_replay?: DigitalTwinSnapshot[];
  risk_replay?: DigitalTwinSnapshot[];
  workflow_replay?: DigitalTwinSnapshot[];
  recommendation_replay?: DigitalTwinSnapshot[];
  combined_timeline?: DigitalTwinSnapshot[];
  snapshot_counts?: Record<string, number>;
}

function isEntityChatFallbackEnabled(): boolean {
  return String(import.meta.env.VITE_ENABLE_ENTITY_CHAT_FALLBACK ?? '').trim().toLowerCase() === 'true';
}

async function fetchEntityListViaRest(
  path: string,
  tenantId: string,
  patientId: string
): Promise<EntityListItem[] | null> {
  const base = getBase();
  const headers: Record<string, string> = {};
  const apiKey = getApiKey();
  if (apiKey) headers['X-API-Key'] = apiKey;
  const params = new URLSearchParams({ tenantId: tenantId || 'default', patientId: patientId.trim() });
  try {
    const res = await fetch(`${base}${path}?${params}`, {
      method: 'GET',
      headers: Object.keys(headers).length ? headers : undefined,
    });
    // A 404 here means the ENDPOINT is absent, not that the patient has no
    // findings. Returning [] made "this data source does not exist" render as
    // "0 risk factors / 0 open alerts" -- a clinician reads that as
    // reassurance. Treat it as unavailable and let the caller say so.
    if (!res.ok) return null;
    const body = await safeJson(res) as EntityListItem[];
    return Array.isArray(body) ? body : [];
  } catch {
    return null;
  }
}

async function fetchEntityList(
  path: string,
  tenantId: string,
  patientId: string
): Promise<EntityListItem[]> {
  if (!isMarchHttpApiConfigured() || !patientId?.trim()) return [];

  const restItems = await fetchEntityListViaRest(path, tenantId, patientId.trim());
  if (restItems !== null) return restItems;

  if (isMarchMode()) {
    if (isEntityChatFallbackEnabled()) {
      return fetchEntityListViaMarch(path, patientId.trim());
    }
    return [];
  }

  throw new Error(`Failed to load (${path})`);
}

export async function fetchAssessments(tenantId: string, patientId: string): Promise<EntityListItem[]> {
  return fetchEntityList('/v1/assessments', tenantId, patientId);
}

export async function fetchAlerts(tenantId: string, patientId: string): Promise<EntityListItem[]> {
  return fetchEntityList('/v1/alerts', tenantId, patientId);
}

export async function fetchRedFlags(tenantId: string, patientId: string): Promise<EntityListItem[]> {
  return fetchEntityList('/v1/redflags', tenantId, patientId);
}

export async function fetchExports(tenantId: string, patientId: string): Promise<EntityListItem[]> {
  return fetchEntityList('/v1/exports', tenantId, patientId);
}

export async function fetchSimulations(tenantId: string, patientId: string): Promise<EntityListItem[]> {
  return fetchEntityList('/v1/simulations', tenantId, patientId);
}

export async function fetchVisits(tenantId: string, patientId: string): Promise<EntityListItem[]> {
  return fetchEntityList('/v1/visits', tenantId, patientId);
}

export async function fetchDigitalTwinOverview(
  patientId: string,
  conversationId?: string,
): Promise<DigitalTwinOverview> {
  const base = getBase();
  const trimmedPatientId = patientId.trim();
  if (!isMarchHttpApiConfigured() || !trimmedPatientId) {
    return {
      available: false,
      summary: '',
      patient: null,
      risk: null,
      workflow: null,
      recommendation: null,
      patient_replay: [],
      risk_replay: [],
      workflow_replay: [],
      recommendation_replay: [],
      combined_timeline: [],
      snapshot_counts: {},
    };
  }
  const params = new URLSearchParams();
  if (conversationId?.trim()) params.set('conversation_id', conversationId.trim());
  const qs = params.toString();
  const res = await fetchWithAuth(
    `${base}/api/v1/digital-twin/patient/${encodeURIComponent(trimmedPatientId)}/overview${qs ? `?${qs}` : ''}`,
    {
      method: 'GET',
      extraHeaders: jsonHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text && text.length < 200 ? text : `Failed to load digital twin (${res.status})`);
  }
  const body = await safeJson(res) as Partial<DigitalTwinOverview>;
  return {
    available: body.available === true,
    summary: typeof body.summary === 'string' ? body.summary : '',
    patient: body.patient ?? null,
    risk: body.risk ?? null,
    workflow: body.workflow ?? null,
    recommendation: body.recommendation ?? null,
    patient_replay: Array.isArray(body.patient_replay) ? body.patient_replay : [],
    risk_replay: Array.isArray(body.risk_replay) ? body.risk_replay : [],
    workflow_replay: Array.isArray(body.workflow_replay) ? body.workflow_replay : [],
    recommendation_replay: Array.isArray(body.recommendation_replay) ? body.recommendation_replay : [],
    combined_timeline: Array.isArray(body.combined_timeline) ? body.combined_timeline : [],
    snapshot_counts:
      body.snapshot_counts && typeof body.snapshot_counts === 'object'
        ? (body.snapshot_counts as Record<string, number>)
        : {},
  };
}

export async function fetchDigitalTwinReplay(
  patientId: string,
  limit: number = 12,
): Promise<DigitalTwinSnapshot[]> {
  const base = getBase();
  const trimmedPatientId = patientId.trim();
  if (!isMarchHttpApiConfigured() || !trimmedPatientId) return [];
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await fetchWithAuth(
    `${base}/api/v1/digital-twin/patient/${encodeURIComponent(trimmedPatientId)}/replay?${params.toString()}`,
    {
      method: 'GET',
      extraHeaders: jsonHeaders(),
    },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text && text.length < 200 ? text : `Failed to load digital twin replay (${res.status})`);
  }
  const body = await safeJson(res) as { snapshots?: DigitalTwinSnapshot[] };
  return Array.isArray(body.snapshots) ? body.snapshots : [];
}

/**
 * GET /v1/patients?tenantId=... — list patients for the UI. Uses same auth as submitCommand.
 * When Command API is disabled or request fails, caller should fall back to demo data.
 */
export async function fetchPatients(tenantId: string = 'default'): Promise<PatientListItem[]> {
  const dedicatedPatients = (import.meta.env.VITE_PATIENTS_API_URL ?? '').toString().trim();
  const base = dedicatedPatients.replace(/\/$/, '') || getMarchHttpApiBase();
  if (!isMarchHttpApiConfigured() && !dedicatedPatients) return [];
  if (isMarchMode() || (import.meta.env.VITE_PATIENTS_API_URL ?? '').toString().trim().length > 0) {
    const fromApi = await fetchPatientsFromAwsApi(base, tenantId);
    if (fromApi.length > 0) return fromApi;
    // Optional local-only fallback when explicitly set.
    return defaultMarchPatients();
  }
  const headers: Record<string, string> = {};
  const apiKey = getApiKey();
  if (apiKey) headers['X-API-Key'] = apiKey;
  const res = await fetch(`${base}/v1/patients?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'GET',
    headers: Object.keys(headers).length ? headers : undefined,
  });
  if (!res.ok) return [];
  const body = await safeJson(res) as PatientListItem[];
  return Array.isArray(body) ? body : [];
}

function defaultMarchPatients(): PatientListItem[] {
  const raw = (import.meta.env.VITE_MARCH_DEFAULT_PATIENTS ?? '').toString().trim();
  if (!raw) return [];
  const parsed = raw
    .split(';')
    .map((entry: string) => entry.trim())
    .filter(Boolean)
    .map((entry: string) => {
      const [mrn, name] = entry.split(':');
      return {
        mrn: (mrn ?? '').trim(),
        name: (name ?? mrn ?? '').trim(),
      };
    })
    .filter((p: { mrn: string; name: string }) => p.mrn.length > 0)
    .map((p: { mrn: string; name: string }) => ({
      mrn: p.mrn,
      name: p.name,
      status: 'Active',
      risk: 'Moderate',
    }));
  return parsed;
}

function normalizePatientRows(value: unknown): PatientListItem[] {
  const rows = extractPatientCandidates(value)
    .map((item) => toPatientListItem(item))
    .filter((item): item is PatientListItem => !!item);
  const deduped = new Map<string, PatientListItem>();
  for (const row of rows) {
    if (!deduped.has(row.mrn)) deduped.set(row.mrn, row);
  }
  return Array.from(deduped.values());
}

function extractPatientCandidates(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is Record<string, unknown> => !!v && typeof v === 'object');
  }
  if (!value || typeof value !== 'object') return [];
  const obj = value as Record<string, unknown>;
  if (obj.resourceType === 'Bundle' && Array.isArray(obj.entry)) {
    return obj.entry
      .map((entry) => (entry && typeof entry === 'object' ? (entry as Record<string, unknown>).resource : null))
      .filter((v): v is Record<string, unknown> => !!v && typeof v === 'object');
  }
  const keys = ['patients', 'data', 'items', 'results', 'entries'];
  for (const key of keys) {
    const candidate = obj[key];
    if (Array.isArray(candidate)) {
      return candidate.filter((v): v is Record<string, unknown> => !!v && typeof v === 'object');
    }
    if (candidate && typeof candidate === 'object') {
      const nested = candidate as Record<string, unknown>;
      for (const nestedKey of keys) {
        const nestedCandidate = nested[nestedKey];
        if (Array.isArray(nestedCandidate)) {
          return nestedCandidate.filter((v): v is Record<string, unknown> => !!v && typeof v === 'object');
        }
      }
    }
  }
  return [obj];
}

function toPatientListItem(value: Record<string, unknown>): PatientListItem | null {
  const identifier = Array.isArray(value.identifier) ? value.identifier : [];
  const identifierMrn = identifier
    .map((it) => (it && typeof it === 'object' ? String((it as Record<string, unknown>).value ?? '').trim() : ''))
    .find((it) => it.length > 0);
  const mrn = [
    value.mrn,
    value.medical_record_number,
    value.medicalRecordNumber,
    value.patient_id,
    value.patientId,
    value.id,
    identifierMrn,
  ]
    .map((it) => String(it ?? '').trim())
    .find((it) => it.length > 0);
  if (!mrn) return null;

  const nameField = value.name;
  let name = '';
  if (typeof nameField === 'string') {
    name = nameField.trim();
  } else if (Array.isArray(nameField) && nameField.length > 0) {
    const first = nameField[0];
    if (typeof first === 'string') name = first.trim();
    if (first && typeof first === 'object') {
      const firstObj = first as Record<string, unknown>;
      const given = Array.isArray(firstObj.given) ? firstObj.given.map((g) => String(g)).join(' ') : String(firstObj.given ?? '').trim();
      const family = String(firstObj.family ?? '').trim();
      name = `${given} ${family}`.trim();
      if (!name) name = String(firstObj.text ?? '').trim();
    }
  } else if (nameField && typeof nameField === 'object') {
    const nameObj = nameField as Record<string, unknown>;
    const given = Array.isArray(nameObj.given) ? nameObj.given.map((g) => String(g)).join(' ') : String(nameObj.given ?? '').trim();
    const family = String(nameObj.family ?? '').trim();
    name = `${given} ${family}`.trim();
    if (!name) name = String(nameObj.text ?? '').trim();
  }
  if (!name) {
    name = String(value.full_name ?? value.fullName ?? value.display ?? `Patient ${mrn}`).trim();
  }

  const status = String(value.status ?? (value.active === false ? 'Inactive' : 'Active')).trim() || 'Active';
  const risk = String(value.risk ?? value.risk_level ?? value.riskLevel ?? 'Unknown').trim() || 'Unknown';
  return { mrn, name, status, risk };
}

async function fetchPatientsFromAwsApi(base: string, tenantId: string): Promise<PatientListItem[]> {
  const headers: Record<string, string> = {};
  const apiKey = getApiKey();
  if (apiKey) headers['X-API-Key'] = apiKey;
  const bearer = getBearer();
  if (bearer) headers.Authorization = `Bearer ${bearer}`;

  const t = encodeURIComponent((tenantId || 'default').trim());
  const endpointOverrides = (import.meta.env.VITE_PATIENTS_API_PATHS ?? '')
    .toString()
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean);
  const endpoints = endpointOverrides.length > 0
    ? endpointOverrides
    : [
        `/v1/patients?tenantId=${t}`,
        `/api/v1/patients?tenantId=${t}`,
        `/patients?tenantId=${t}`,
        `/api/patients?tenantId=${t}`,
        `/fhir/Patient?_count=200`,
      ];
  let authFailed = false;

  for (const endpoint of endpoints) {
    const url = endpoint.startsWith('http') ? endpoint : `${base}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    try {
      const res = await fetchWithAuth(url, {
        method: 'GET',
        extraHeaders: Object.keys(headers).length ? headers : undefined,
      });
      if (res.status === 401) {
        authFailed = true;
        continue;
      }
      if (!res.ok) continue;
      const body = await safeJson(res) as unknown;
      const patients = normalizePatientRows(body);
      if (patients.length > 0) return patients;
    } catch {
      // Try the next endpoint.
    }
  }
  if (authFailed) {
    throw new Error('Authentication required. Please sign in or provide a bearer token.');
  }
  return [];
}

export interface HealthResult {
  ok: boolean;
  status?: string;
}

const COMMAND_API_HEALTH_TIMEOUT_MS = 5_000;

async function fetchHealthWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), COMMAND_API_HEALTH_TIMEOUT_MS);
  try {
    return await fetch(url, { method: 'GET', signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * GET /v1/health to check if the Command API is reachable. No API key required.
 * Use for "Command API connected" indicator when VITE_COMMAND_API_URL is set.
 */
export async function checkCommandApiHealth(): Promise<HealthResult> {
  if (!isMarchHttpApiConfigured()) return { ok: false };
  const base = getBase();
  try {
    const healthPaths = isMarchMode() ? ['/health', '/v1/health'] : ['/v1/health', '/health'];
    for (const p of healthPaths) {
      const res = await fetchHealthWithTimeout(`${base}${p}`);
      if (!res.ok) continue;
      const body = await safeJson(res) as { status?: string };
      return { ok: true, status: body.status ?? 'up' };
    }
    return { ok: false };
  } catch {
    return { ok: false };
  }
}

export interface SubmitResponse {
  runId: string;
  streamUrl: string;
}

/** Stream path for a conversation — shared by submit and refresh-resume. */
export function buildChatStreamUrl(conversationId: string, patientId?: string): string {
  const qs = new URLSearchParams({ conversation_id: conversationId });
  if (patientId?.trim()) qs.set('patient_id', patientId.trim());
  return `/api/v1/chat/stream?${qs.toString()}`;
}

export interface ChatStreamStatus {
  active: boolean;
  pending: boolean;
  /**
   * False when the probe could not reach a verdict (offline, auth not ready,
   * non-200). Callers MUST distinguish this from a definitive "not running":
   * treating an unanswerable probe as "no run" is what discards a resumable
   * answer, so on `ok === false` keep the marker and retry.
   */
  ok: boolean;
}

/**
 * Refresh-resume probe (2026-08-11): is a run still executing server-side for
 * this conversation (`active`), or is a submitted message still waiting for
 * its stream to be opened (`pending`)? Fail-safe: any error reads as neither,
 * so resume quietly degrades to the persisted-turn recovery path.
 */
export async function fetchChatStreamStatus(
  conversationId: string,
  patientId?: string
): Promise<ChatStreamStatus> {
  const unknown: ChatStreamStatus = { active: false, pending: false, ok: false };
  if (!isMarchHttpApiConfigured() || !conversationId.trim()) return unknown;
  try {
    const qs = new URLSearchParams({ conversation_id: conversationId.trim() });
    if (patientId?.trim()) qs.set('patient_id', patientId.trim());
    const res = await fetchWithAuth(`${getBase()}/api/v1/chat/active?${qs.toString()}`, {
      method: 'GET',
      extraHeaders: jsonHeaders(),
    });
    if (!res.ok) return unknown;
    const body = (await safeJson(res)) as { active?: boolean; pending?: boolean };
    return { active: body.active === true, pending: body.pending === true, ok: true };
  } catch {
    return unknown;
  }
}

export async function submitCommand(
  command: string,
  tenantId: string,
  patientId: string,
  options?: Record<string, unknown>,
  conversationId?: string,
): Promise<SubmitResponse> {
  const base = getBase();
  if (!isMarchHttpApiConfigured()) throw new Error('Command API URL not configured');
  if (isMarchMode()) {
    const runId = conversationId || `conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const body = {
      message: command,
      patient_id: patientId?.trim() || null,
      conversation_id: runId,
      tenant_id: tenantId || 'default',
      options: options ?? null,
    };
    const res = await fetchWithAuth(`${base}/api/v1/chat/message`, {
      method: 'POST',
      extraHeaders: { 'Content-Type': 'application/json', ...streamExtraHeaders() },
      body: JSON.stringify(body),
    });
    if (res.status === 404) {
      pendingLegacyStreams.set(runId, {
        command,
        tenantId: tenantId || 'default',
        patientId: patientId?.trim() || '',
        options,
      });
      const streamUrl = 'legacy-post:/chat';
      recordRunSubmitted({
        runId,
        command,
        tenantId: tenantId || 'default',
        patientId: patientId?.trim() || null,
        streamUrl,
      });
      return {
        runId,
        streamUrl,
      };
    }
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`March API ${res.status}: ${text || res.statusText}`);
    }
    const payload = await safeJson(res) as { conversation_id?: string };
    const resolvedConvId = payload.conversation_id || runId;
    const streamUrl = buildChatStreamUrl(resolvedConvId, patientId);
    recordRunSubmitted({
      runId: resolvedConvId,
      command,
      tenantId: tenantId || 'default',
      patientId: patientId?.trim() || null,
      streamUrl,
    });
    return {
      runId: resolvedConvId,
      streamUrl,
    };
  }

  const res = await fetchWithAuth(`${base}/v1/commands`, {
    method: 'POST',
    extraHeaders: jsonHeaders(),
    body: JSON.stringify({ command, tenantId, patientId, options: options ?? null }),
  });
  if (!res.ok) {
    const text = await res.text();
    if (res.status === 401) {
      throw new Error('Invalid or missing API key. Set VITE_COMMAND_API_KEY if the backend requires auth.');
    }
    if (res.status === 429) {
      const retryAfter = res.headers.get('Retry-After');
      const msg = retryAfter
        ? `Too many requests. Try again in ${retryAfter} seconds.`
        : 'Too many requests. Try again later.';
      throw new Error(msg);
    }
    if (res.status === 403) {
      const msg = text && text.length < 200 ? text : 'This feature is not enabled.';
      throw new Error(msg);
    }
    throw new Error(`Command API ${res.status}: ${text || res.statusText}`);
  }
  const body = await safeJson(res) as SubmitResponse;
  if (!body.runId || !body.streamUrl) throw new Error('Invalid Command API response');
  recordRunSubmitted({
    runId: body.runId,
    command,
    tenantId: tenantId || 'default',
    patientId: patientId?.trim() || null,
    streamUrl: body.streamUrl,
  });
  return body;
}

export async function cancelCommandRun(
  conversationId: string,
  patientId?: string
): Promise<{ status: string; conversation_id?: string }> {
  const base = getBase();
  if (!isMarchHttpApiConfigured()) throw new Error('Command API URL not configured');
  const conv = conversationId.trim();
  if (!conv) throw new Error('Missing conversation ID');
  const params = new URLSearchParams({ conversation_id: conv });
  const pid = (patientId ?? '').trim();
  if (pid) params.set('patient_id', pid);
  const res = await fetchWithAuth(`${base}/api/v1/chat/cancel?${params.toString()}`, {
    method: 'POST',
    extraHeaders: jsonHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Cancel failed (${res.status}): ${text || res.statusText}`);
  }
  const body = await safeJson(res) as { status?: string; conversation_id?: string };
  return {
    status: typeof body.status === 'string' ? body.status : 'unknown',
    conversation_id: typeof body.conversation_id === 'string' ? body.conversation_id : undefined,
  };
}

export interface WidgetEvent {
  type: string;
  data: Record<string, unknown>;
}

export interface StreamCallbacks {
  onWidget: (widget: WidgetEvent) => void;
  onSseEvent?: (event: {
    channel?: string;
    event?: string;
    data?: Record<string, unknown>;
    type?: string;
    done?: boolean;
  }) => void;
  onError?: (message: string) => void;
  onDone?: () => void;
  /** Called when the stream reconnects after an auth retry (401 refresh). */
  onReconnecting?: () => void;
}

/**
 * Open SSE stream for a runId. streamUrl is the path from the submit response (e.g. /v1/commands/stream?runId=...).
 * Calls onWidget for each "widget" event, onError for "error" events, onDone when stream ends.
 */
export interface OpenStreamOptions {
  /**
   * Explicit Last-Event-ID for this stream request, overriding the
   * module-level cursor. Refresh-resume passes '0' so the backend replays
   * the whole run — the reloaded client has no render state to append to.
   */
  lastEventId?: string;
}

export function openCommandStream(
  runId: string,
  streamUrl: string,
  callbacks: StreamCallbacks,
  opts?: OpenStreamOptions
): () => void {
  const base = getBase();
  if (streamUrl.startsWith('legacy-post:/chat')) {
    const pending = pendingLegacyStreams.get(runId);
    if (!pending) {
      callbacks.onError?.('Missing legacy stream payload.');
      callbacks.onDone?.();
      return () => {};
    }
    pendingLegacyStreams.delete(runId);

    const controller = new AbortController();
    let closed = false;
    let doneSent = false;
    let synthesizedSent = false;
    let finalText = '';
    let latestMetadata: Record<string, unknown> | undefined;

    void (async () => {
      try {
        const headers = streamHeaders();
        const resp = await fetch(`${base}/chat`, {
          method: 'POST',
          headers,
          signal: controller.signal,
          body: JSON.stringify({
            message: pending.command,
            patient_id: pending.patientId || null,
            conversation_id: runId,
            tenant_id: pending.tenantId || 'default',
            options: pending.options ?? null,
          }),
        });
        if (!resp.ok || !resp.body) {
          const text = await resp.text();
          callbacks.onError?.(`March API ${resp.status}: ${text || resp.statusText}`);
          callbacks.onDone?.();
          return;
        }

        await readSseEvents(resp, (payload) => {
          if (closed) return;
          callbacks.onSseEvent?.(payload);
          if (payload.type && payload.data) {
            callbacks.onWidget({ type: payload.type, data: payload.data });
            synthesizedSent = true;
            return;
          }
          const eventName = payload.event ?? '';
          const data = payload.data ?? {};
          if (eventName === 'stream.token') {
            const token = String((data as { token?: unknown }).token ?? '');
            if (token) finalText = `${finalText}${token}`;
            return;
          }
          const synthesizedWidget = synthesizeWidgetFromSse(eventName, data);
          if (synthesizedWidget) {
            callbacks.onWidget(synthesizedWidget);
            synthesizedSent = true;
            return;
          }
          if (eventName === 'response.metadata') {
            latestMetadata = data;
            return;
          }
          if (eventName === 'stream.error') {
            recordRunFailed(runId);
            callbacks.onError?.(streamErrorDisplayText(data));
            return;
          }
          if (eventName === 'stream.end' && !doneSent) {
            const text = finalText.trim();
            const isRawJson = text.startsWith('[') || text.startsWith('{');
            if (!synthesizedSent && text && !isRawJson) {
              callbacks.onWidget({
                type: 'W_SUMMARY_CLINICAL',
                data: {
                  summary: text,
                  keyPoints: [],
                  runId,
                  ...(latestMetadata ? { metadata: latestMetadata } : {}),
                },
              });
            }
            recordRunCompleted(runId);
            doneSent = true;
            callbacks.onDone?.();
          }
        });
        if (!doneSent && !closed) {
          doneSent = true;
          callbacks.onDone?.();
        }
      } catch (error) {
        if (closed) return;
        const message =
          error instanceof Error ? error.message : 'Connection lost. Check your network or try again.';
        callbacks.onError?.(message);
        if (!doneSent) callbacks.onDone?.();
      }
    })();

    return () => {
      if (closed) return;
      closed = true;
      controller.abort();
    };
  }
  const rawUrl = streamUrl.startsWith('http') ? streamUrl : `${base}${streamUrl}`;
  const controller = new AbortController();
  let closed = false;
  let doneSent = false;
  let synthesizedSent = false;
  let finalText = '';
  let latestMetadata: Record<string, unknown> | undefined;

  void (async () => {
    try {
      const resp = await fetchWithAuth(rawUrl, {
        method: 'GET',
        extraHeaders: {
          ...streamExtraHeaders(),
          ...(opts?.lastEventId !== undefined ? { 'Last-Event-ID': opts.lastEventId } : {}),
        },
        signal: controller.signal,
        onAuthRetry: () => callbacks.onReconnecting?.() ?? callbacks.onError?.(STREAM_RECONNECTING_MESSAGE),
      });
      if (!resp.ok) {
        const text = await resp.text();
        const safeMessage =
          resp.status === 401
            ? SESSION_EXPIRED_MESSAGE
            : resp.status === 403
              ? 'You do not have permission to access this resource.'
              : `March API ${resp.status}: ${text || resp.statusText}`;
        callbacks.onError?.(safeMessage);
        if (!doneSent) callbacks.onDone?.();
        return;
      }
      if (!resp.body) {
        callbacks.onError?.('Stream response missing body');
        if (!doneSent) callbacks.onDone?.();
        return;
      }

      await readSseEvents(resp, (payload) => {
        if (closed) return;

        if (payload.sseEvent === 'widget') {
          try {
            const widgetPayload = payload as { type?: string; data?: Record<string, unknown> };
            callbacks.onWidget({
              type: widgetPayload.type ?? 'W_UNKNOWN',
              data: widgetPayload.data ?? {},
            });
          } catch {
            callbacks.onError?.('Invalid widget payload');
          }
          return;
        }

        callbacks.onSseEvent?.(payload);

        if (payload.type && payload.data) {
          callbacks.onWidget({ type: payload.type, data: payload.data });
          synthesizedSent = true;
          return;
        }

        const eventName = payload.event ?? '';
        const data = payload.data ?? {};
        if (eventName === 'stream.token') {
          const token = String((data as { token?: unknown }).token ?? '');
          if (token) finalText = `${finalText}${token}`;
          return;
        }
        const synthesizedWidget = synthesizeWidgetFromSse(eventName, data);
        if (synthesizedWidget) {
          callbacks.onWidget(synthesizedWidget);
          synthesizedSent = true;
          return;
        }
        if (eventName === 'response.metadata') {
          latestMetadata = data;
          return;
        }
        if (eventName === 'stream.error') {
          recordRunFailed(runId);
          callbacks.onError?.(streamErrorDisplayText(data));
          return;
        }
        if (eventName === 'stream.end') {
          if (!synthesizedSent && finalText.trim()) {
            callbacks.onWidget({
              type: 'W_SUMMARY_CLINICAL',
              data: {
                summary: finalText.trim(),
                keyPoints: [],
                runId,
                ...(latestMetadata ? { metadata: latestMetadata } : {}),
              },
            });
            synthesizedSent = true;
          }
          if (!doneSent) {
            recordRunCompleted(runId);
            doneSent = true;
            callbacks.onDone?.();
          }
        }
      });
      if (!doneSent && !closed) {
        doneSent = true;
        callbacks.onDone?.();
      }
    } catch (error) {
      if (closed) return;
      const message =
        error instanceof Error ? error.message : 'Connection lost. Check your network or try again.';
      callbacks.onError?.(message);
      if (!doneSent) callbacks.onDone?.();
    }
  })();

  return () => {
    if (closed) return;
    closed = true;
    controller.abort();
  };
}

async function fetchEntityListViaMarch(path: string, patientId: string): Promise<EntityListItem[]> {
  const commandByPath: Record<string, string> = {
    '/v1/assessments': '/latest_assessment',
    '/v1/alerts': '/alerts limit=10',
    '/v1/redflags': '/redflags',
    '/v1/exports': '/list_exports',
    '/v1/simulations': '/simulation_result',
    '/v1/visits': '/summary window=last_30_days',
  };
  const message = commandByPath[path];
  if (!message) return [];
  const text = await runMarchTextQuery(message, patientId);
  return textToEntityItems(text, path);
}

async function runMarchTextQuery(message: string, patientId: string): Promise<string> {
  const base = getBase();
  if (!isMarchHttpApiConfigured()) return '';
  const conversationId = `entity-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const postResp = await fetchWithAuth(`${base}/api/v1/chat/message`, {
    method: 'POST',
    extraHeaders: { 'Content-Type': 'application/json', ...streamExtraHeaders() },
    body: JSON.stringify({
      message,
      patient_id: patientId || null,
      conversation_id: conversationId,
    }),
  });
  if (postResp.status === 404) {
    const legacyResp = await fetch(`${base}/chat`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        message,
        patient_id: patientId || null,
        conversation_id: conversationId,
      }),
    });
    if (!legacyResp.ok || !legacyResp.body) return '';
    let answer = '';
    let hadError = false;
    await readSseEvents(legacyResp, (payload) => {
      if (payload.event === 'stream.token') {
        const token = String((payload.data as { token?: unknown })?.token ?? '').trim();
        if (token) answer = `${answer}${answer ? ' ' : ''}${token}`;
      } else if (payload.event === 'stream.error') {
        hadError = true;
      }
    });
    return hadError ? '' : answer.trim();
  }
  if (!postResp.ok) return '';

  const qs = new URLSearchParams({ conversation_id: conversationId });
  if (patientId) qs.set('patient_id', patientId);
  const streamResp = await fetchWithAuth(`${base}/api/v1/chat/stream?${qs.toString()}`, {
    method: 'GET',
    extraHeaders: streamExtraHeaders(),
  });
  if (!streamResp.ok || !streamResp.body) return '';

  let answer = '';
  let hadError = false;
  await readSseEvents(streamResp, (payload) => {
    if (payload.event === 'stream.token') {
      const token = String((payload.data as { token?: unknown })?.token ?? '').trim();
      if (token) answer = `${answer}${answer ? ' ' : ''}${token}`;
    } else if (payload.event === 'stream.error') {
      hadError = true;
    }
  });
  return hadError ? '' : answer.trim();
}

function textToEntityItems(text: string, path: string): EntityListItem[] {
  if (!text.trim()) return [];
  const typeMap: Record<string, string> = {
    '/v1/assessments': 'assessment',
    '/v1/alerts': 'alert',
    '/v1/redflags': 'redflag',
    '/v1/exports': 'export',
    '/v1/simulations': 'simulation',
    '/v1/visits': 'visit',
  };
  const entityType = typeMap[path] ?? 'item';
  const lines = text
    .split('\n')
    .map((l) => l.replace(/^\s*[-*•]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 12);
  const source = lines.length > 0 ? lines : [text.trim()];
  const now = new Date();
  const date = now.toLocaleDateString();
  const time = now.toLocaleTimeString();
  return source.map((summary, idx) => ({
    id: `${entityType}-${idx + 1}`,
    date,
    time,
    type: entityType,
    title: `${entityType.toUpperCase()} ${idx + 1}`,
    summary,
    chips: ['live', entityType],
  }));
}
